/**
 * ResourcePanel
 * -------------
 * 
 * October 2026
 */
import CostPanel from './CostPanel';

import * as React from "react";

import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { Paginator } from "primereact/paginator";

const resourceWarnings = {
    'name': function(newName) { return newName === "" ? "Name is required" : null },
    'initialAmount': function(newAmount) { return newAmount <= 0 ? "Initial amount must be positive" : null }
}

/**
 * Configuration sub-panel for editing a resource's attributes.
 * 
 * @param {Object} config               - Configuration object for the simulation model. 
 * @param {Function} setConfig          - Setter for config object.
 * @param {Object} resource             - Attributes for resource element being edited.
 * @param {Object} configWarnings       - Verification warnings for entity types.
 * @param {Function} setConfigWarnings  - Setter for verification warnings.
 * @param {Boolean} reachedConfig       - Flag for when users enter the configuration panel.
 */
function ResourcePanel({ config, setConfig, resource, configWarnings, setConfigWarnings, reachedConfig }) {
    if (!resource) return null;

    // Manages current section index to display.
    const [sectionIndex, setSectionIndex] = React.useState(0);

    // Manages the current resource cost being rendered.
    const [selectedCostIndex, setCostIndex] = React.useState(-1);

    /**
     * Resets attribute panel to the "Basic Info" section.
     * Triggers when current resource element switches.
     */
    React.useEffect(() => {
        setSectionIndex(0);
    }, [resource.gid]);

    /**
     * Verifies that the resource element's parameters were set correctly once an action diagram is initially parsed.
     */
    React.useEffect(() => {
        if (reachedConfig) {
            for (const [gid, resource] of Object.entries(config.resources)) {
                updateResourceWarnings(gid, 'initialAmount', resourceWarnings['initialAmount'](resource.initialAmount));
            }
        }
    // eslint-disable-next-line
    }, [reachedConfig]);

    /**
     * Updates warnings for a resource element's parameters.
     * 
     * @param {String} gid          - Resource's identifier.
     * @param {String} parameter    - Element parameter linked to warning.
     * @param {String} warning      - Updated warning message.
     */
    const updateResourceWarnings = (gid, parameter, warning) => {
        setConfigWarnings(prev => {
            const newWarnings = {...prev, resources: {...prev.resources, [gid]: {...prev.resources[gid]}}};
            if (warning) {
                newWarnings.resources[gid][parameter] = warning;
            } else {
                delete newWarnings.resources[gid][parameter];
            }

            return newWarnings;
        });
    }

    /**
     * Updates the resource element's name.
     * 
     * @param {String} newName  - Updated name.
     * @param {String} gid      - Resource element's identifier.
     */
    const handleResourceName = (newName, gid) => {
        updateResourceWarnings(gid, 'name', resourceWarnings['name'](newName));

        const newConfig = {...config, resources: {...config.resources, [gid]: {...config.resources[gid], name: newName}}};
        setConfig(newConfig);
    }

    /**
     * Updates the resource element's initial amount value.
     * 
     * @param {int} newAmount   - Updated initial amount.
     * @param {String} gid      - Resource element's identifier.
     */
    const handleResourceInitialAmount = (newAmount, gid) => {
        updateResourceWarnings(gid, 'initialAmount', resourceWarnings['initialAmount'](newAmount));

        const newConfig = {...config, resources: {...config.resources, [gid]: {...config.resources[gid], initialAmount: newAmount}}};
        setConfig(newConfig);
    }

    /**
     * Updatees a resource element's chosen queue discipline.
     * 
     * @param {String} queue    - New queue discipline being set.
     * @param {String} gid      - Resource element's identifier.
     */
    const handleQueue = (queue, gid) => {
        let newConfig = {...config};
        newConfig.resources[gid].queueDiscipline = queue;

        if (queue === 'Priority') {
            newConfig.resources[gid].priorityRanks = Object.fromEntries(Object.keys(config.entity_types).map(gid => [gid, 0]));
            setConfig(newConfig);
        }
        else if (queue === 'FIFO') {
            newConfig.resources[gid].priorityRanks = {};
            setConfig(newConfig);
        }
    }

    /**
     * Assigns ranks to an entity type for a resource element's priority queue.
     * 
     * @param {int} newRank             - Updated rank.
     * @param {String} entityTypeGid    - The entity type's identifier.
     * @param {String} resourceGid      - The resource element's identifier.
     */
    const handleRank = (newRank, entityTypeGid, resourceGid) => {
        const newRanks = {...config.resources[resourceGid].priorityRanks, [entityTypeGid]: newRank};
        const newResource = {...config.resources[resourceGid], priorityRanks: newRanks};
        const newResources = {...config.resources, [resourceGid]: newResource};
        const newConfig = {...config, resources: newResources};
        setConfig(newConfig);
    }

    /**
     * New index for a panel section.
     * 
     * @param {Number} newSection 
     */
    const handleSectionChange = (newSection) => {
        setSectionIndex(newSection);
    }

    /**
     * Panel section for a resource element's basic attributes.
     * 
     * @returns Child component for the "Basic Info" section.
     */
    const basicInfoSection = () => {
        return (
            <div>
                <div>
                    <label>Gid: {resource.gid}</label>
                </div>
                <Divider/>
                <div>
                    <label style={{ marginRight: '1rem'}}>Name:</label>
                    <InputText value={resource.name} onChange={(e) => handleResourceName(e.target.value, resource.gid)} invalid={!!configWarnings.resources?.[resource.gid]?.name} style={{ width: '150px', marginRight: '0.5rem' }}/>
                </div>
                <Divider/>
                <div>
                    <label style={{ marginRight: '1rem'}}>Amount:</label>
                    <InputNumber value={resource.initialAmount} onValueChange={(event) => handleResourceInitialAmount(event.target.value, resource.gid)} placeholder="Enter value" min={0} useGrouping={false} invalid={!!configWarnings.resources?.[resource.gid]?.initialAmount} inputStyle={{ width: '150px' }}/>
                </div>
            </div>
        )
    }

    /**
     * Panel section for a resource element's queue attributes.
     * 
     * @returns Child component for the "Queue System" section.
     */
    const queueSection = () => {
        return (
            <div>
                <div style={{ marginBottom: '1rem'}}>
                    <label style={{ marginRight: '1rem'}}>Queue Discipline:</label>
                    <Dropdown value={resource.queueDiscipline || "FIFO"} options={["FIFO", "Priority"]} onChange={(event) => handleQueue(event.value, resource.gid)}/>
                </div>
                {resource.queueDiscipline === 'Priority' &&
                    Object.entries(config.entity_types || {}).map(([gid, entityTypes]) => (
                        <div key={gid} style={{ marginBottom: '0.5rem'}}>
                            <label style={{ marginRight: '1rem'}}>{entityTypes.name} Rank</label>
                            <InputNumber value={resource.priorityRanks?.[gid] ?? ""} onValueChange={(event) => handleRank(event.target.value, gid, resource.gid)} placeholder="Enter value" min={0} useGrouping={false} inputStyle={{ width: '110px' }}/>
                        </div>
                    ))
                }
            </div>
        )
    }

    /**
     * Panel section for a resource element's cost attributes.
     * 
     * @returns Child component for the "Cost" section.
     */
    const costSection = () => {
        return <CostPanel config={config} setConfig={setConfig} resource={resource} selectedCostIndex={selectedCostIndex} setCostIndex={setCostIndex}/>;
    }

    const sections = [basicInfoSection(), queueSection(), costSection()];
    const dropdownSections = [
        { label: "Basic Info", value: 0 },
        { label: "Queue Logic", value: 1},
        { label: "Costs", value: 2}
    ];

    return (
        <Card style={{ width: "450px", height: "500px" }}>
            <Dropdown value={sectionIndex} onChange={(event) => setSectionIndex(event.value)} options={dropdownSections} optionLabel="label" optionValue="value" style={{ marginBottom: "2rem" }}
                valueTemplate={(option) => (<span style={{ fontSize: "1.17em", fontWeight: "bold" }}>{option?.label}</span>)}/>
            <div style={{ height: "300px", overflow: "auto" }}>{sections[sectionIndex]}</div>
            <Paginator first={sectionIndex} rows={1} totalRecords={3} onPageChange={(event) => handleSectionChange(event.first)} template={{ layout: 'PrevPageLink CurrentPageReport NextPageLink' }}/>
        </Card>
    )
}

export default ResourcePanel;